import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { useToast } from "@/components/ui/use-toast";

interface AddSuppliesSheetProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: Id<"serviceLocations">;
  onSuppliesAdded?: () => void;
}

export function AddSuppliesSheet({
  isOpen,
  onClose,
  locationId,
  onSuppliesAdded,
}: AddSuppliesSheetProps) {
  const [selectedSupplyId, setSelectedSupplyId] = useState<Id<"supplies"> | "manual">("manual");
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState("");
  const [showCustomUnit, setShowCustomUnit] = useState(false);
  const [customUnit, setCustomUnit] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    quantity: 0,
    unit: "",
    lastRestocked: "",
    minimumRequired: 0,
    brand: "",
    vendor: "",
    costPerUnit: 0,
    storageLocation: "",
    category: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const masterSupplies = useQuery(api.supplies.list) || [];
  const addSupplies = useMutation(api.serviceLocations.addLocationSupplies);

  // Handle master supply selection
  const handleSupplySelection = (supplyId: string) => {
    setSelectedSupplyId(supplyId as Id<"supplies"> | "manual");

    if (supplyId && supplyId !== "manual") {
      const selectedSupply = masterSupplies.find((s: any) => s._id === supplyId);
      if (selectedSupply) {
        setFormData((prev) => ({
          ...prev,
          name: selectedSupply.itemName,
          category: selectedSupply.category || "",
          brand: selectedSupply.brand || "",
          unit: selectedSupply.unit,
          costPerUnit: selectedSupply.costPerUnit || 0,
          vendor: selectedSupply.vendor || "",
        }));
        // Clear custom category/unit states when master supply is selected
        setShowCustomCategory(false);
        setCustomCategory("");
        setShowCustomUnit(false);
        setCustomUnit("");
      }
    } else {
      // Clear auto-populated fields if "Manual Entry" is selected
      setFormData((prev) => ({
        ...prev,
        name: "",
        category: "",
        brand: "",
        unit: "",
        costPerUnit: 0,
        vendor: "",
      }));
      // Also clear custom states when switching to manual entry
      setShowCustomCategory(false);
      setCustomCategory("");
      setShowCustomUnit(false);
      setCustomUnit("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Use final values for category and unit (custom if selected)
    const finalCategory = showCustomCategory ? customCategory : formData.category;
    const finalUnit = showCustomUnit ? customUnit : formData.unit;

    if (!formData.name.trim() || !finalUnit.trim() || !formData.lastRestocked) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await addSupplies({
        locationId,
        supplyId: selectedSupplyId && selectedSupplyId !== "manual" ? selectedSupplyId : undefined,
        name: formData.name.trim(),
        quantity: formData.quantity,
        unit: finalUnit.trim(),
        lastRestocked: formData.lastRestocked,
        minimumRequired: formData.minimumRequired > 0 ? formData.minimumRequired : undefined,
        brand: formData.brand.trim() || undefined,
        vendor: formData.vendor.trim() || undefined,
        costPerUnit: formData.costPerUnit > 0 ? formData.costPerUnit : undefined,
        storageLocation: formData.storageLocation.trim() || undefined,
        category: finalCategory || undefined,
      });

      toast({
        title: "Success!",
        description: "Supplies added successfully",
      });

      // Reset form
      setSelectedSupplyId("manual");
      setFormData({
        name: "",
        quantity: 0,
        unit: "",
        lastRestocked: "",
        minimumRequired: 0,
        brand: "",
        vendor: "",
        costPerUnit: 0,
        storageLocation: "",
        category: "",
      });
      setCustomCategory("");
      setShowCustomCategory(false);
      setCustomUnit("");
      setShowCustomUnit(false);

      onSuppliesAdded?.();
      onClose();
    } catch (error) {
      console.error("Error adding supplies:", error);
      toast({
        title: "Error",
        description: "Failed to add supplies",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedSupplyId("manual");
      setFormData({
        name: "",
        quantity: 0,
        unit: "",
        lastRestocked: "",
        minimumRequired: 0,
        brand: "",
        vendor: "",
        costPerUnit: 0,
        storageLocation: "",
        category: "",
      });
      setCustomCategory("");
      setShowCustomCategory(false);
      setCustomUnit("");
      setShowCustomUnit(false);
      onClose();
    }
  };

  // Get today's date in YYYY-MM-DD format for default value
  const today = new Date().toISOString().split("T")[0];

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Add Supplies</SheetTitle>
          <SheetDescription>
            Add new supplies to track inventory for this location.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)] pr-6">
          <form onSubmit={handleSubmit} className="space-y-6 py-6">
            {/* Supply Selection */}
            <div className="space-y-2">
              <Label htmlFor="supply-select">Select Supply (Optional)</Label>
              <Select value={selectedSupplyId} onValueChange={handleSupplySelection}>
                <SelectTrigger>
                  <SelectValue placeholder="Select from master list or enter manually" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual Entry</SelectItem>
                  {masterSupplies.map((supply: any) => (
                    <SelectItem key={supply._id} value={supply._id}>
                      {supply.itemName} - {supply.unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Separator />

            {/* Supply Information (from Master List) */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground">
                Supply Information {selectedSupplyId !== "manual" && "(from Master List)"}
              </h3>

              <div className="space-y-2">
                <Label htmlFor="name">
                  Item Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., All-Purpose Cleaner, Paper Towels"
                  required
                  disabled={selectedSupplyId !== "manual"}
                  className={selectedSupplyId !== "manual" ? "bg-muted" : ""}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                {selectedSupplyId === "manual" ? (
                  <Select
                    value={formData.category}
                    onValueChange={(value) => {
                      setShowCustomCategory(value === "custom");
                      setFormData({ ...formData, category: value });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cleaning-chemicals">Cleaning Chemicals</SelectItem>
                      <SelectItem value="paper-products">Paper Products</SelectItem>
                      <SelectItem value="tools-equipment">Tools & Equipment</SelectItem>
                      <SelectItem value="safety-supplies">Safety Supplies</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="office-supplies">Office Supplies</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="e.g., Cleaning Chemicals"
                    disabled={true}
                    className="bg-muted"
                  />
                )}
              </div>

              {showCustomCategory && selectedSupplyId === "manual" && (
                <div className="space-y-2">
                  <Label htmlFor="custom-category">Custom Category</Label>
                  <Input
                    id="custom-category"
                    placeholder="Enter custom category"
                    value={customCategory}
                    onChange={(e) => setCustomCategory(e.target.value)}
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="brand">Brand</Label>
                  <Input
                    id="brand"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    placeholder="e.g., Clorox"
                    disabled={selectedSupplyId !== "manual"}
                    className={selectedSupplyId !== "manual" ? "bg-muted" : ""}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vendor">Vendor</Label>
                  <Input
                    id="vendor"
                    value={formData.vendor}
                    onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                    placeholder="e.g., ABC Supply Co."
                    disabled={selectedSupplyId !== "manual"}
                    className={selectedSupplyId !== "manual" ? "bg-muted" : ""}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="unit">
                    Unit <span className="text-red-500">*</span>
                  </Label>
                  {selectedSupplyId === "manual" ? (
                    <Select
                      value={formData.unit}
                      onValueChange={(value) => {
                        setShowCustomUnit(value === "custom");
                        setFormData({ ...formData, unit: value });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ea">Each (ea)</SelectItem>
                        <SelectItem value="box">Box</SelectItem>
                        <SelectItem value="case">Case</SelectItem>
                        <SelectItem value="gal">Gallon (gal)</SelectItem>
                        <SelectItem value="L">Liter (L)</SelectItem>
                        <SelectItem value="oz">Ounce (oz)</SelectItem>
                        <SelectItem value="lb">Pound (lb)</SelectItem>
                        <SelectItem value="roll">Roll</SelectItem>
                        <SelectItem value="pack">Pack</SelectItem>
                        <SelectItem value="bag">Bag</SelectItem>
                        <SelectItem value="bottle">Bottle</SelectItem>
                        <SelectItem value="can">Can</SelectItem>
                        <SelectItem value="carton">Carton</SelectItem>
                        <SelectItem value="doz">Dozen (doz)</SelectItem>
                        <SelectItem value="pallet">Pallet</SelectItem>
                        <SelectItem value="qt">Quart (qt)</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="unit"
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      placeholder="e.g., gal, box, case"
                      required
                      disabled={true}
                      className="bg-muted"
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="costPerUnit">Cost Per Unit</Label>
                  <Input
                    id="costPerUnit"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.costPerUnit}
                    onChange={(e) =>
                      setFormData({ ...formData, costPerUnit: parseFloat(e.target.value) || 0 })
                    }
                    placeholder="0.00"
                    disabled={selectedSupplyId !== "manual"}
                    className={selectedSupplyId !== "manual" ? "bg-muted" : ""}
                  />
                </div>
              </div>

              {showCustomUnit && selectedSupplyId === "manual" && (
                <div className="space-y-2">
                  <Label htmlFor="custom-unit">Custom Unit</Label>
                  <Input
                    id="custom-unit"
                    placeholder="Enter custom unit"
                    value={customUnit}
                    onChange={(e) => setCustomUnit(e.target.value)}
                  />
                </div>
              )}
            </div>

            <Separator />

            {/* Location-Specific Details */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground">
                Location-Specific Details
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="quantity">
                    Quantity on Hand <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.quantity}
                    onChange={(e) =>
                      setFormData({ ...formData, quantity: parseFloat(e.target.value) || 0 })
                    }
                    placeholder="0"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="minimumRequired">Reorder Level</Label>
                  <Input
                    id="minimumRequired"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.minimumRequired}
                    onChange={(e) =>
                      setFormData({ ...formData, minimumRequired: parseFloat(e.target.value) || 0 })
                    }
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastRestocked">
                  Last Restocked <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="lastRestocked"
                  type="date"
                  value={formData.lastRestocked}
                  onChange={(e) => setFormData({ ...formData, lastRestocked: e.target.value })}
                  max={today}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="storageLocation">Storage Location</Label>
                <Input
                  id="storageLocation"
                  value={formData.storageLocation}
                  onChange={(e) => setFormData({ ...formData, storageLocation: e.target.value })}
                  placeholder="e.g., Storage Room A, Shelf 3"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? "Adding..." : "Add Supplies"}
              </Button>
            </div>
          </form>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
